import { useCallback, useEffect, useRef, useState } from 'react';

interface UsePollingOptions<T> {
  enabled?: boolean;
  args?: unknown[];
  refetchInterval: number | ((data: T | undefined) => number | false);
}

interface UsePollingReturn {
  isPolling: boolean;
  pollingError: boolean;
  refetch: () => void;
}

function usePolling<T>(
  fetchFn: (...args: unknown[]) => Promise<T | null>,
  data: T | undefined,
  options: UsePollingOptions<T>
): UsePollingReturn {
  const { enabled = true, args, refetchInterval } = options;
  const [isPolling, setIsPolling] = useState(false);
  const [pollingError, setPollingError] = useState(false);
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingActiveRef = useRef(false);
  const dataRef = useRef<T | undefined>(data);
  const refetchIntervalRef = useRef(refetchInterval);
  const argsRef = useRef(args);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  useEffect(() => {
    refetchIntervalRef.current = refetchInterval;
  }, [refetchInterval]);
  useEffect(() => {
    argsRef.current = args;
  }, [args]);

  const stopPolling = useCallback(() => {
    pollingActiveRef.current = false;
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const stopPollingWithError = useCallback(() => {
    stopPolling();
    setPollingError(true);
  }, [stopPolling]);

  const getInterval = useCallback((): number | false => {
    const ri = refetchIntervalRef.current;
    if (typeof ri === 'function') return ri(dataRef.current);
    return ri;
  }, []);

  const runPollingCycle = useCallback(
    (callArgs: unknown[]) => {
      stopPolling();
      pollingActiveRef.current = true;
      setIsPolling(true);

      const scheduleNext = (): void => {
        if (!pollingActiveRef.current) return;
        const nextInterval = getInterval();
        if (nextInterval === false) {
          stopPolling();
          return;
        }
        pollingTimerRef.current = setTimeout(() => {
          tick();
        }, nextInterval);
      };

      const tick = async (): Promise<void> => {
        if (!pollingActiveRef.current) return;
        try {
          const result = await fetchFn(...callArgs);
          dataRef.current = result ?? undefined;
          scheduleNext();
        } catch {
          stopPollingWithError();
        }
      };

      tick();
    },
    [fetchFn, stopPolling, stopPollingWithError, getInterval]
  );

  const serializedArgs = args ? JSON.stringify(args) : undefined;
  useEffect(() => {
    if (enabled && refetchIntervalRef.current && args) {
      setPollingError(false);
      runPollingCycle(args);
    } else {
      stopPolling();
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, serializedArgs]);

  const refetch = useCallback(() => {
    const currentArgs = argsRef.current ?? [];
    setPollingError(false);
    runPollingCycle(currentArgs);
  }, [runPollingCycle]);

  return { isPolling, pollingError, refetch };
}

export default usePolling;
